import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PaginatedResponse, RelationshipProposal } from '../types';
import {
  fetchRelationshipProposals,
  approveRelationshipProposal,
  rejectRelationshipProposal,
  bulkApproveRelationshipProposals,
  bulkRejectRelationshipProposals,
} from '../services/api';
import { encodeId } from '../utils/hashids';
import Pager from '../components/Pager';

/**
 * Phase 2.6 — human review queue for AI-proposed relationship changes.
 * A proposal never touches leads.relationship on its own; approving here
 * is the only thing (besides a CSV import match) that does.
 */
const RelationshipProposalsPage: React.FC = () => {
  const history = useHistory();
  const [data, setData]       = useState<PaginatedResponse<RelationshipProposal> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [page, setPage]       = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [working, setWorking]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRelationshipProposals({ page });
      setData(res);
      setSelected(new Set());
    } catch {
      setError('Failed to load relationship proposals');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id: number) =>
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const toggleAll = () => {
    const ids = data?.data.map(p => p.id) ?? [];
    setSelected(prev => prev.size === ids.length ? new Set() : new Set(ids));
  };

  const handleApprove = async (proposal: RelationshipProposal) => {
    setWorking(true);
    try { await approveRelationshipProposal(proposal.lead_id, proposal.id); await load(); }
    catch { alert('Failed to approve proposal'); }
    finally { setWorking(false); }
  };

  const handleReject = async (proposal: RelationshipProposal) => {
    setWorking(true);
    try { await rejectRelationshipProposal(proposal.lead_id, proposal.id); await load(); }
    catch { alert('Failed to reject proposal'); }
    finally { setWorking(false); }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setWorking(true);
    try { await bulkApproveRelationshipProposals(Array.from(selected)); await load(); }
    catch { alert('Bulk approve failed'); }
    finally { setWorking(false); }
  };

  const handleBulkReject = async () => {
    if (selected.size === 0) return;
    setWorking(true);
    try { await bulkRejectRelationshipProposals(Array.from(selected)); await load(); }
    catch { alert('Bulk reject failed'); }
    finally { setWorking(false); }
  };

  return (
    <div className="list-page">
      <div className="page-hd">
        <div className="page-hd-left">
          <h1 className="page-title">Relationship Proposals</h1>
        </div>
        {selected.size > 0 && (
          <div className="page-hd-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selected.size} selected</span>
            <button className="btn btn-primary" disabled={working} onClick={handleBulkApprove}>Approve selected</button>
            <button className="btn btn-secondary" disabled={working} onClick={handleBulkReject}>Reject selected</button>
          </div>
        )}
      </div>

      <div className="data-card">
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : error ? (
          <div className="spinner">{error}</div>
        ) : (
          <div className="data-card-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={!!data && data.data.length > 0 && selected.size === data.data.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>Lead</th>
                  <th>Proposed change</th>
                  <th>Confidence</th>
                  <th>Evidence</th>
                  <th>Proposed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data && data.data.length > 0 ? data.data.map(p => (
                  <tr key={p.id}>
                    <td>
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} />
                    </td>
                    <td>
                      <a
                        href="#"
                        onClick={e => { e.preventDefault(); history.push(`/leads/${encodeId(p.lead_id)}`); }}
                      >
                        {p.lead?.name ?? `Lead #${p.lead_id}`}
                      </a>
                      <div className="td-muted" style={{ fontSize: 11 }}>{p.lead?.mobile ?? p.lead?.phone}</div>
                    </td>
                    <td>
                      <span className="td-muted">{p.from_relationship ?? 'prospect'}</span>
                      {' → '}
                      <strong>{p.to_relationship}</strong>
                    </td>
                    <td>{p.confidence != null ? `${Math.round(p.confidence * 100)}%` : '—'}</td>
                    <td style={{ maxWidth: 360 }}>
                      <span title={p.evidence_text ?? ''} style={{
                        display: 'block', overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', fontSize: 12, fontStyle: 'italic',
                      }}>
                        "{p.evidence_text ?? '—'}"
                      </span>
                    </td>
                    <td>{new Date(p.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="row-actions">
                        <button className="btn btn-primary btn-sm" disabled={working} onClick={() => handleApprove(p)}>Approve</button>
                        <button className="btn btn-secondary btn-sm" disabled={working} onClick={() => handleReject(p)}>Reject</button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr className="table-empty-row">
                    <td colSpan={7}>No pending relationship proposals — the AI hasn't found any post-sale evidence that needs review right now.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {data && data.data.length > 0 && (
        <Pager
          currentPage={data.current_page}
          lastPage={data.last_page}
          total={data.total}
          label="Proposals"
          onChange={setPage}
        />
      )}
    </div>
  );
};

export default RelationshipProposalsPage;
